import type { ImageFormats } from '@eneco/cms-types';
import { stegaClean } from '@sanity/client/stega';

import { IMAGE_FORMATS_TABLE } from './image-formats.gen';
import type { SanityQueryOptions, SanityQueryRunner } from './query-runner';
import type { SanityPage } from './sanity-types';

/**
 * The CMS schema's `image-formats.json` (`ImageFormatsFile` in its `contract/mapping-exports.d.ts`), copied
 * into `image-formats.gen.ts` because this lib may not import the schema package.
 */
export interface ImageFormatsTable {
  readonly version: number;
  /** Which format group a type's image field uses; `when` names the block settings that select it. */
  readonly fields: readonly {
    readonly sanityType: string;
    readonly field: string;
    readonly group: string;
    readonly when?: Readonly<Record<string, string>>;
  }[];
  /** The site's `siteName` → group → its formats, in Sitecore's order. */
  readonly sites: Readonly<
    Record<string, Readonly<Record<string, readonly { format: string; width: number; height: number }[]>>>
  >;
}

const FORMAT_NAMES: ReadonlySet<string> = new Set(['default', 'small', 'medium', 'large', 'extraLarge', 'widescreen']);

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** The image object an image field holds: the `imageWithAlt` wrapper's `image`, or the field itself. */
const imageOf = (value: unknown): Record<string, unknown> | undefined => {
  if (!isObject(value)) return undefined;
  if (isObject(value.image) && isObject(value.image.asset)) return value.image;
  return isObject(value.asset) ? value : undefined;
};

/**
 * The formats Sitecore cut a block's image field into on a site: the field's format group, picked by the
 * block's settings where its type merges renderings of different groups (the `Image` block's
 * `aspectRatio`), then the site's sizes for that group. A site without the group gets none, as in Sitecore.
 */
export const formatsFor = (
  table: ImageFormatsTable,
  siteName: string,
  block: Record<string, unknown>,
  field: string,
): ImageFormats | undefined => {
  const row = table.fields.find(
    entry =>
      entry.sanityType === block._type &&
      entry.field === field &&
      Object.entries(entry.when ?? {}).every(([setting, value]) => stegaClean(block[setting]) === value),
  );
  const sizes = row ? table.sites[siteName]?.[row.group] : undefined;
  if (!sizes?.length) return undefined;
  return Object.fromEntries(
    sizes
      .filter(size => FORMAT_NAMES.has(size.format))
      .map(size => [size.format, { width: size.width, height: size.height }]),
  ) as ImageFormats;
};

const withFormats = (value: unknown, table: ImageFormatsTable, siteName: string, isPage = false): unknown => {
  if (Array.isArray(value)) return value.map(item => withFormats(item, table, siteName));
  if (!isObject(value)) return value;
  // Not the page's own image: Sitecore's page meta and structured data show it whole, not as a card.
  const typed = !isPage && typeof value._type === 'string';
  return Object.fromEntries(
    Object.entries(value).map(([key, member]) => {
      const walked = withFormats(member, table, siteName);
      const image = typed ? imageOf(walked) : undefined;
      const formats = image ? formatsFor(table, siteName, value, key) : undefined;
      if (!image || !formats) return [key, walked];
      const marked = { ...image, formats };
      return [key, image === walked ? marked : { ...(walked as Record<string, unknown>), image: marked }];
    }),
  );
};

const SITE_NAME_QUERY = `*[_id == $site][0].siteName`;

/**
 * Gives every image on a page the formats Sitecore cut it into, from the CMS schema's image formats
 * table, which `mappers.ts` then builds the image from. Sitecore sent each image already cut, in the
 * sizes the field's format group sets for the site; Sanity stores the original, so the sizes come from
 * the table, by the type and field the image is on and the page's site.
 *
 * Runs per request, on the page with its shared content filled in, so images there get theirs too. The
 * site's name is asked once per site.
 */
export const createImageFormatResolver = (
  runner: SanityQueryRunner,
  table: ImageFormatsTable = IMAGE_FORMATS_TABLE,
) => {
  // Per perspective, so a draft read never answers a published one.
  const siteNames = new Map<string, Promise<string | undefined>>();
  const siteNameOf = (site: string, options: SanityQueryOptions) => {
    const key = `${JSON.stringify(options.perspective ?? 'published')}:${site}`;
    if (!siteNames.has(key)) {
      const name = runner
        .query<unknown>(SITE_NAME_QUERY, { site }, options)
        .then(value => (typeof value === 'string' && value.length > 0 ? stegaClean(value) : undefined));
      // A failed lookup, or a site without a name yet, is asked again on the next request.
      const forget = () => siteNames.delete(key);
      name.then(value => value === undefined && forget(), forget);
      siteNames.set(key, name);
    }
    return siteNames.get(key) as Promise<string | undefined>;
  };

  return async (page: SanityPage, options: SanityQueryOptions = {}): Promise<SanityPage> => {
    const site = isObject(page.site) ? page.site._ref : undefined;
    if (typeof site !== 'string') return page;
    // Without the site's name the images keep the formats their components ask for, as before.
    const siteName = await siteNameOf(site, options).catch(() => undefined);
    return siteName && table.sites[siteName] ? (withFormats(page, table, siteName, true) as SanityPage) : page;
  };
};
